import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  "aria-hidden": "true",
  class: "label-content"
}
const _hoisted_3 = ["id"]

import {
  computed,
  ref,
  onMounted,
  getCurrentInstance,
  watch,
  nextTick,
} from "vue";
import { IonButton, IonIcon, IonSpinner } from "@ionic/vue";

export type Button = "primary" | "secondary" | "tertiary" | "textOnly";
type Size = "default" | "small" | "large";
type IconPostion = "start" | "end";

interface Props {
  size: Size;
  type: Button;
  width?: string;
  label: string;
  ariaLabel?: string;
  icon?: string;
  iconPosition?: IconPostion;
  disabled?: boolean;
  isLoading?: boolean;
  ariaRoleDescription?: string;
  showLoadingSkeleton?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppButton',
  props: {
    size: { default: "default" },
    type: { default: "primary" },
    width: {},
    label: {},
    ariaLabel: {},
    icon: {},
    iconPosition: {},
    disabled: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    ariaRoleDescription: { default: "" },
    showLoadingSkeleton: { type: Boolean }
  },
  emits: ["click"],
  setup(__props: any, { emit: __emit }) {

_useCssVars(_ctx => ({
  "2867fca9": (cssWidth.value)
}))

const props = __props;

const uid = ref<number | null | undefined>(null);

onMounted(async () => {
  uid.value = getCurrentInstance()?.uid;
  setTimeout(() => {
    setNativeButtonAttribute("aria-label", props.ariaLabel || "");
  }, 1500);
});

watch(
  () => props.ariaRoleDescription,
  (newVal) => {
    setNativeButtonAttribute("aria-description", newVal || "");
  }
);

watch(
  () => props.ariaLabel,
  async (newVal) => {
    setNativeButtonAttribute("aria-label", newVal || "");
  }
);

watch(
  () => props.showLoadingSkeleton,
  async (isShow) => {
    if (!isShow) {
      await nextTick(() => {
        setTimeout(() => {
          setNativeButtonAttribute("aria-label", props.ariaLabel || "");
        }, 1500);
      });
    }
  }
);

/**
 * Sets the attribute of the native button inside the ion-button element.
 * This is done mainly for a11y purposes.
 * Otherwise, an update to an ARIA attribute would be done to the ion-button element,
 * but not to the native button element.
 * @param attr The attribute name
 * @param value The value of the attribute
 */
const setNativeButtonAttribute = (attr: string, value: string): void => {
  const btnInnerEl = document
    .getElementById(`button-${uid.value}`)
    ?.shadowRoot?.querySelector("button");
  if (btnInnerEl) {
    btnInnerEl.setAttribute(attr, value);
  }
};

const emits = __emit;

const cssWidth = computed(() => props.width);

const disabledClass = computed(() => (props.disabled ? "disabled" : undefined));
const isLoadingClass = computed(() =>
  props.isLoading ? "is-loading" : undefined
);
const buttonCustomClass = computed(() =>
  props.width ? "button-custom" : undefined
);
const typeClass = computed(() => props.type);

const spinnerColour = computed(() => {
  switch (props.type) {
    case "primary":
      return undefined;
    case "secondary":
      return "primary";
    case "tertiary":
      return "tertiary";
    default:
      return undefined;
  }
});

const spinnerPositionClass = computed(() => {
  if (!props.icon && !props.iconPosition) {
    return undefined;
  }
  switch (props.iconPosition) {
    case "start":
      return "shift-left";
    case "end":
      return "shift-right";
    default:
      return undefined;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_ion_skeleton_text = _resolveComponent("ion-skeleton-text")!

  return (_ctx.showLoadingSkeleton)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_component_ion_skeleton_text, {
          animated: true,
          style: {"width":"312px","height":"44px"}
        })
      ]))
    : (_openBlock(), _createBlock(_unref(IonButton), {
        key: 1,
        size: _ctx.size,
        class: _normalizeClass([[typeClass.value, disabledClass.value, isLoadingClass.value, buttonCustomClass.value], "ion-text-wrap app-button"]),
        disabled: _ctx.disabled,
        id: `button-${uid.value}`,
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emits('click'))),
        "data-test": "button"
      }, {
        default: _withCtx(() => [
          (_ctx.icon)
            ? (_openBlock(), _createBlock(_unref(IonIcon), {
                key: 0,
                icon: _ctx.icon,
                slot: _ctx.iconPosition,
                class: _normalizeClass(_ctx.iconPosition),
                "aria-hidden": "true",
                "data-test": "icon"
              }, null, 8, ["icon", "slot", "class"]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("span", {
              class: "label",
              id: `button-label-${uid.value}`,
              "data-test": "label"
            }, _toDisplayString(_ctx.label), 9, _hoisted_3),
            (_ctx.isLoading)
              ? (_openBlock(), _createBlock(_unref(IonSpinner), {
                  key: 0,
                  name: "lines-small",
                  "aria-hidden": "true",
                  class: _normalizeClass(["loading-icon", [spinnerPositionClass.value]]),
                  color: spinnerColour.value,
                  "data-test": "loading-icon"
                }, null, 8, ["color", "class"]))
              : _createCommentVNode("", true)
          ])
        ]),
        _: 1
      }, 8, ["size", "class", "disabled", "id"]))
}
}

})